import type { ArticleComponentProps } from "@components/Article/Article";
import Path from "@core/FileProvider/Path/Path";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ArticleViewService from "@core-ui/ContextServices/views/articleView/ArticleViewService";
import debounceFunction from "@core-ui/debounceFunction";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import BranchUpdaterService, {
	type OnBranchUpdateListener,
} from "@ext/git/actions/Branch/BranchUpdaterService/logic/BranchUpdaterService";
import OnBranchUpdateCaller from "@ext/git/actions/Branch/BranchUpdaterService/model/OnBranchUpdateCaller";
import { PublishEmitter } from "@ext/git/actions/Publish/logic/PublishEmitter";
import { useIsRevision } from "@ext/git/actions/Revisions/logic/hooks/useIsRevision";
import ArticleDiffModeView from "@ext/git/core/Diff/components/ArticleDiffModeView";
import LoadingWithDiffBottomBar from "@ext/git/core/Diff/components/LoadingWithDiffBottomBar";
import {
	setDiffEnabled,
	setDoublePanelLocked,
	setSideBarData,
} from "@ext/git/core/Diff/components/store/DiffViewModeStore";
import useFetchDiffData from "@ext/git/core/Diff/logic/hooks/useFetchDiffData";
import { useResetArticleView } from "@ext/git/core/Diff/logic/hooks/useResetArticleView";
import { FileStatus } from "@ext/Watchers/model/FileStatus";
import type { JSONContent } from "@tiptap/core";
import { useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";

const DEBOUNCE_TIME = 200;
const DEBOUNCE_SYMBOL = Symbol();

const ArticleDiffViewWrapper = ({ data, isReadOnly: propsIsReadOnly }: ArticleComponentProps<"diff">) => {
	const { sideBarData, scope, oldScope } = data;

	const catalogName = useCatalogPropsStore((state) => state.data?.name);
	const isRevision = useIsRevision();
	const [, forceUpdate] = useReducer((x) => x + 1, 0);

	const apiUrlCreator = ApiUrlCreatorService.value;

	const isAdded = sideBarData.data.status === FileStatus.new;
	const isDeleted = sideBarData.data.status === FileStatus.delete;
	const isAddedOrDeleted = isAdded || isDeleted;
	const isReadOnly = propsIsReadOnly || isDeleted || isRevision;

	const newPath = sideBarData.data.filePath.path;
	const oldPath = sideBarData.data.filePath.oldPath;
	const fullArticlePath = Path.join(catalogName, newPath);

	const content = useRef<string>(null);
	const editTree = useRef<JSONContent>(null);

	const oldContent = useRef<string>(null);
	const oldEditTree = useRef<JSONContent>(null);

	const [isLoading, setIsLoading] = useState(true);

	const fetchDiffData = useFetchDiffData({ isAdded, isDeleted, scope, oldScope, newPath, oldPath });

	const tryGetNewData = async () => {
		const haveNewDataAlready = editTree.current && typeof content.current === "string";
		if (!isRevision && haveNewDataAlready) return;

		if (isRevision) {
			const haveOldDataAlready = oldEditTree.current && typeof oldContent.current === "string";
			const haveNewDataAlready = isAdded || (editTree.current && typeof content.current === "string");
			const haveOldDataAlreadyOrNotNeeded = isDeleted || haveOldDataAlready;
			if (haveNewDataAlready && haveOldDataAlreadyOrNotNeeded) return;
			setIsLoading(true);

			try {
				const { newData, oldData } = await fetchDiffData(null);
				oldContent.current = oldData?.content;
				oldEditTree.current = oldData?.editTree;
				content.current = newData?.content;
				editTree.current = newData?.editTree;
			} catch (error) {
				console.error(error);
			} finally {
				setIsLoading(false);
			}

			return;
		}

		if (isAddedOrDeleted) {
			setIsLoading(true);
			const { newData, oldData } = await fetchDiffData(null);
			if (isAdded) {
				content.current = newData?.content;
				editTree.current = newData?.editTree;
			} else {
				oldContent.current = oldData?.content;
				oldEditTree.current = oldData?.editTree;
			}
			setIsLoading(false);
			return;
		}

		const haveOldDataAlready = oldEditTree.current && typeof oldContent.current === "string";
		const onlyNew = !!haveOldDataAlready;

		setIsLoading(true);
		const { newData, oldData } = await fetchDiffData(onlyNew);

		content.current = newData?.content;
		editTree.current = newData?.editTree;
		if (!onlyNew) {
			oldContent.current = oldData?.content;
			oldEditTree.current = oldData?.editTree;
		}
		setIsLoading(false);
	};

	useResetArticleView();

	// biome-ignore lint/correctness/useExhaustiveDependencies: expected
	useLayoutEffect(() => {
		setDiffEnabled(true);
		setDoublePanelLocked(isDeleted);
		setSideBarData(sideBarData);
		void tryGetNewData();

		return () => {
			setDiffEnabled(false);
			setDoublePanelLocked(false);
			setSideBarData(null);
		};
	}, []);

	useEffect(() => {
		const onPublishFinish = () => {
			oldContent.current = content.current;
			oldEditTree.current = editTree.current;
			forceUpdate();
		};

		const onDiscard: OnBranchUpdateListener = (_, caller) => {
			if (caller !== OnBranchUpdateCaller.DiscardNoReset && caller !== OnBranchUpdateCaller.MergeRequest) return;
			oldContent.current = content.current;
			oldEditTree.current = editTree.current;
			forceUpdate();
		};

		const finishToken = PublishEmitter.events.on("finish", onPublishFinish);
		BranchUpdaterService.addListener(onDiscard);
		return () => {
			PublishEmitter.events.off(finishToken);
			BranchUpdaterService.removeListener(onDiscard);
		};
	}, []);

	if (isLoading) return <LoadingWithDiffBottomBar />;

	return (
		<ArticleDiffModeView
			articlePath={newPath}
			changeType={sideBarData.data.status}
			key={newPath}
			newContent={content.current}
			newEditTree={editTree.current}
			newScope={scope}
			oldArticlePath={oldPath === newPath ? undefined : oldPath}
			oldContent={oldContent.current}
			oldEditTree={oldEditTree.current}
			oldScope={oldScope}
			onMonacoUpdate={(content) => {
				if (isReadOnly) return;
				editTree.current = null;
				debounceFunction(
					DEBOUNCE_SYMBOL,
					async () => {
						await FetchService.fetch(apiUrlCreator.setArticleContent(fullArticlePath, true), content);
					},
					DEBOUNCE_TIME,
				);
			}}
			onViewModeChange={() => {
				ArticleViewService.useArticleDefaultStyles = true;
				if (isReadOnly) return;
				void tryGetNewData();
			}}
			onWysiwygUpdate={() => {
				content.current = null;
			}}
			readOnly={isReadOnly}
		/>
	);
};

export default ArticleDiffViewWrapper;
